import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { isLocalBootstrapVersion } from "../utils/masterDataVersion";

import type {
  AllocationMasterDataSnapshot,
  AllocationMasterRecord,
  AllocationMasterVersionInfo,
  AllocationMasterAvailableChanges,
} from "../types/allocationMasterData";

interface ApiAllocationRecord {
  id: number;
  entry_key: string;
  order_number: string;
  plant: string;
  plant_area: string;
  manufacturer: string;
  remark?: string;
  manual_state?: "created" | "updated" | null;
}

interface BundledAllocationRecord {
  auftragsnummer?: unknown;
  kategorie?: unknown;
  bezeichnung?: unknown;
  hersteller?: unknown;
  bemerkung?: unknown;
}

interface ApiVersion {
  id: number;
  version_number: number;
  source_filename: string | null;
  source_hash: string;
  source_date: string | null;
  activated_at: string | null;
}

interface ApiSnapshot {
  version: ApiVersion;
  allocations: ApiAllocationRecord[];
  manual_revision?: string | null;
}

interface ApiStatus {
  available: boolean;
  version: ApiVersion | null;
  manual_revision?: string | null;
}

interface StoredAllocationMasterData {
  key: "current";
  records: AllocationMasterRecord[];
  version: AllocationMasterVersionInfo;
  savedAt: string;
}

const BUNDLED_ALLOCATION_MASTER_URL = "/data/kontierungen.json";
const SERVER_SNAPSHOT_URL = "/api/allocation-master/snapshot";
const SERVER_STATUS_URL = "/api/allocation-master/status";
const DATABASE_NAME = "materialentnahme-allocation-master";
const DATABASE_VERSION = 1;
const STORE_NAME = "allocation-master";
const STORE_KEY = "current";

let memorySnapshot: AllocationMasterDataSnapshot | null = null;
let loadingPromise: Promise<AllocationMasterDataSnapshot> | null = null;
let bootstrapRefreshPromise: Promise<AllocationMasterDataSnapshot | null> | null = null;

function mapVersion(version: ApiVersion, manualRevision: string | null = null): AllocationMasterVersionInfo {
  return {
    id: version.id,
    versionNumber: version.version_number,
    sourceFilename: version.source_filename,
    sourceHash: version.source_hash,
    sourceDate: version.source_date,
    activatedAt: version.activated_at,
    manualRevision,
  };
}

function mapRecord(record: ApiAllocationRecord): AllocationMasterRecord {
  return {
    id: record.id,
    entryKey: record.entry_key,
    orderNumber: record.order_number,
    plant: record.plant,
    plantArea: record.plant_area,
    manufacturer: record.manufacturer ?? "",
    remark: record.remark ?? "",
    manualState: record.manual_state ?? null,
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB konnte nicht geöffnet werden."));
  });
}

function hydrateStoredRecord(record: Partial<AllocationMasterRecord>, index: number): AllocationMasterRecord {
  const orderNumber = String(record.orderNumber ?? "").trim().replace(/\s+/g, "");
  return {
    id: Number.isFinite(Number(record.id)) ? Number(record.id) : -(20000 + index),
    entryKey: String(record.entryKey ?? `stored:${index}:${orderNumber}`),
    orderNumber,
    plant: String(record.plant ?? ""),
    plantArea: String(record.plantArea ?? ""),
    manufacturer: String(record.manufacturer ?? ""),
    remark: String(record.remark ?? ""),
    manualState: record.manualState ?? null,
  };
}

async function readStoredSnapshot(): Promise<AllocationMasterDataSnapshot | null> {
  if (!("indexedDB" in globalThis)) return null;
  const database = await openDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const request = database
        .transaction(STORE_NAME, "readonly")
        .objectStore(STORE_NAME)
        .get(STORE_KEY);

      request.onsuccess = () => {
        const value = request.result as StoredAllocationMasterData | undefined;
        if (!value || !Array.isArray(value.records)) {
          resolve(null);
          return;
        }
        resolve({
          records: value.records.map((record, index) => hydrateStoredRecord(record, index)),
          source: "indexed-db",
          version: value.version,
          savedAt: value.savedAt ?? null,
        });
      };
      request.onerror = () =>
        reject(request.error ?? new Error("Kontierungsliste konnte nicht gelesen werden."));
    });
  } finally {
    database.close();
  }
}

async function storeSnapshot(snapshot: AllocationMasterDataSnapshot): Promise<void> {
  if (!("indexedDB" in globalThis)) return;
  const database = await openDatabase();

  try {
    await new Promise<void>((resolve, reject) => {
      const tx = database.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put({
        key: STORE_KEY,
        records: snapshot.records,
        version: snapshot.version,
        savedAt: new Date().toISOString(),
      } satisfies StoredAllocationMasterData);

      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(tx.error ?? new Error("Kontierungsliste konnte nicht gespeichert werden."));
      tx.onabort = () =>
        reject(tx.error ?? new Error("Speichern der Kontierungsliste wurde abgebrochen."));
    });
  } finally {
    database.close();
  }
}

async function loadBundledFallback(): Promise<AllocationMasterDataSnapshot> {
  const response = await fetch(BUNDLED_ALLOCATION_MASTER_URL, { cache: "force-cache", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Lokale Kontierungsliste konnte nicht geladen werden (${response.status}).`);
  const input = await response.json() as unknown;
  if (!Array.isArray(input)) throw new Error("Die lokale Kontierungsliste hat kein gültiges Array-Format.");
  const records: AllocationMasterRecord[] = [];
  for (const [index, raw] of input.entries()) {
    if (!raw || typeof raw !== "object") continue;
    const item = raw as BundledAllocationRecord;
    const orderNumber = typeof item.auftragsnummer === "string" ? item.auftragsnummer.trim().replace(/\s+/g, "") : "";
    const plant = typeof item.kategorie === "string" ? item.kategorie.trim() : "";
    const plantArea = typeof item.bezeichnung === "string" ? item.bezeichnung.trim() : "";
    const manufacturer = typeof item.hersteller === "string" ? item.hersteller.trim() : "";
    const remark = typeof item.bemerkung === "string" ? item.bemerkung.trim() : "";
    if (!/^\d+$/.test(orderNumber) || (!plant && !plantArea)) continue;
    records.push({ id: -(10000 + index), entryKey: `bundled:${index}:${orderNumber}`, orderNumber, plant, plantArea, manufacturer, remark, manualState: null });
  }
  if (records.length === 0) throw new Error("Die lokale Kontierungsliste enthält keine gültigen Einträge.");
  return {
    records,
    source: "bundled",
    version: { id: null, versionNumber: 0, sourceFilename: "kontierungen.json", sourceHash: null, sourceDate: null, activatedAt: null, manualRevision: null },
    savedAt: null,
  };
}

async function fetchServerSnapshot(): Promise<AllocationMasterDataSnapshot> {
  const response = await fetchWithTimeout(SERVER_SNAPSHOT_URL, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }, 12000);

  if (!response.ok) {
    throw new Error(`Kontierungsliste konnte nicht geladen werden (${response.status}).`);
  }

  const data = await response.json() as ApiSnapshot;
  return {
    records: data.allocations.map(mapRecord),
    source: "server",
    version: mapVersion(data.version, data.manual_revision ?? null),
    savedAt: new Date().toISOString(),
  };
}

function scheduleBootstrapConvergence(snapshot: AllocationMasterDataSnapshot): void {
  if (!isLocalBootstrapVersion(snapshot.version) || bootstrapRefreshPromise) return;
  bootstrapRefreshPromise = fetchServerSnapshot()
    .then(async (fresh) => {
      memorySnapshot = fresh;
      await storeSnapshot(fresh).catch(() => undefined);
      window.dispatchEvent(new Event("materialentnahme:allocation-data-updated"));
      window.dispatchEvent(new Event("materialentnahme:status-refresh"));
      return fresh;
    })
    .catch(() => null)
    .finally(() => { bootstrapRefreshPromise = null; });
}

async function loadInternal(): Promise<AllocationMasterDataSnapshot> {
  const stored = await readStoredSnapshot().catch(() => null);

  if (stored) {
    memorySnapshot = stored;

    if (isLocalBootstrapVersion(stored.version)) {
      // Version-0 snapshots are bundled bootstrap data, not an immutable
      // server release. Refresh them from the currently shipped file first so
      // an app update can add manufacturer/remark data even while the backend
      // is unavailable. The server convergence remains best-effort afterwards.
      const bundled = await loadBundledFallback().catch(() => null);
      if (bundled) {
        memorySnapshot = bundled;
        await storeSnapshot(bundled).catch(() => undefined);
        scheduleBootstrapConvergence(bundled);
        return bundled;
      }
      scheduleBootstrapConvergence(stored);
    } else {
      // Echte versionierte lokale Serverstände bleiben bewusst stehen, bis der
      // Benutzer eine erkannte neue Version übernimmt.
      void getAllocationMasterServerStatus()
        .then(async (server) => {
          if (!server) return;
          const sameVersion = server.versionNumber === stored.version.versionNumber;
          const sameRevision = (server.manualRevision ?? null) === (stored.version.manualRevision ?? null);
          if (!sameVersion || !sameRevision) {
            window.dispatchEvent(new Event("materialentnahme:allocation-data-status-updated"));
            return;
          }
          const fresh = await fetchServerSnapshot();
          memorySnapshot = fresh;
          await storeSnapshot(fresh).catch(() => undefined);
        })
        .catch(() => undefined);
    }

    return stored;
  }

  try {
    const server = await fetchServerSnapshot();
    memorySnapshot = server;
    await storeSnapshot(server).catch(() => undefined);
    return server;
  } catch (serverError) {
    try {
      const bundled = await loadBundledFallback();
      memorySnapshot = bundled;
      await storeSnapshot(bundled).catch(() => undefined);
      return bundled;
    } catch {
      throw serverError;
    }
  }
}

export function loadAllocationMasterData(): Promise<AllocationMasterDataSnapshot> {
  if (memorySnapshot) {
    scheduleBootstrapConvergence(memorySnapshot);
    return Promise.resolve(memorySnapshot);
  }
  if (!loadingPromise) {
    loadingPromise = loadInternal().finally(() => {
      loadingPromise = null;
    });
  }
  return loadingPromise;
}

function normalize(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKC")
    .toUpperCase()
    .replace(/[‐‑‒–—−]/g, "-")
    .replace(/[^A-Z0-9ÄÖÜẞ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export async function searchAllocationMasterData(
  query: string,
  limit = 100,
  offset = 0
): Promise<{
  records: AllocationMasterRecord[];
  total: number;
  snapshot: AllocationMasterDataSnapshot;
}> {
  const snapshot = await loadAllocationMasterData();
  const normalized = normalize(query);

  if (!normalized) {
    return {
      records: snapshot.records.slice(offset, offset + limit),
      total: snapshot.records.length,
      snapshot,
    };
  }

  const tokens = normalized.split(" ").filter(Boolean);
  const scored = snapshot.records
    .map((record) => {
      const order = normalize(record.orderNumber);
      const plant = normalize(record.plant);
      const area = normalize(record.plantArea);
      const manufacturer = normalize(record.manufacturer);
      const remark = normalize(record.remark);
      const fields = [order, plant, area, manufacturer, remark];

      // Mehrteilige Suchbegriffe müssen vollständig im selben fachlichen
      // Feld vorkommen. So kann "Band 11" nicht aus "Band 83" plus einer
      // zufälligen "11" in der Auftragsnummer zusammengesetzt werden.
      const matchingField = fields.find(
        (field) => tokens.every((token) => field.includes(token))
      );
      if (!matchingField) return null;

      let score = 0;
      if (order === normalized) score += 100;
      else if (order.startsWith(normalized)) score += 70;
      if (plant === normalized) score += 45;
      else if (plant.startsWith(normalized)) score += 30;
      if (area === normalized) score += 40;
      else if (area.startsWith(normalized)) score += 25;
      if (manufacturer === normalized) score += 40;
      else if (manufacturer.startsWith(normalized)) score += 25;
      if (remark === normalized) score += 20;
      else if (remark.startsWith(normalized)) score += 12;
      if (matchingField.includes(normalized)) score += 20;

      return { record, score };
    })
    .filter(
      (item): item is { record: AllocationMasterRecord; score: number } =>
        item !== null
    )
    .sort(
      (left, right) =>
        right.score - left.score
        || left.record.plant.localeCompare(right.record.plant, "de")
        || left.record.plantArea.localeCompare(right.record.plantArea, "de")
    );

  return {
    records: scored.slice(offset, offset + limit).map((item) => item.record),
    total: scored.length,
    snapshot,
  };
}

export async function refreshAllocationMasterData(): Promise<AllocationMasterDataSnapshot> {
  const fresh = await fetchServerSnapshot();
  memorySnapshot = fresh;
  await storeSnapshot(fresh).catch(() => undefined);
  return fresh;
}

export async function getCachedAllocationMasterData(): Promise<AllocationMasterDataSnapshot | null> {
  if (memorySnapshot) { scheduleBootstrapConvergence(memorySnapshot); return memorySnapshot; }
  const stored = await readStoredSnapshot().catch(() => null);
  if (stored) { memorySnapshot = stored; scheduleBootstrapConvergence(stored); return stored; }
  const bundled = await loadBundledFallback().catch(() => null);
  if (bundled) memorySnapshot = bundled;
  return bundled;
}


export async function getAllocationMasterServerStatus(): Promise<AllocationMasterVersionInfo | null> {
  const response = await fetchWithTimeout(SERVER_STATUS_URL, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }, 5000);
  if (!response.ok) {
    throw new Error(`Kontierungsstatus konnte nicht geprüft werden (${response.status}).`);
  }
  const data = await response.json() as ApiStatus;
  return data.available && data.version ? mapVersion(data.version, data.manual_revision ?? null) : null;
}

export async function getAllocationMasterAvailableChanges(sinceVersion: number, sinceRevision: string | null): Promise<AllocationMasterAvailableChanges> {
  const params = new URLSearchParams({ since_version: String(Math.max(0, sinceVersion)) });
  if (sinceRevision) params.set("since_revision", sinceRevision);
  const response = await fetch(`/api/allocation-master/changes?${params.toString()}`, { credentials: "include", cache: "no-store", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Änderungen konnten nicht geladen werden (${response.status}).`);
  const data = await response.json() as { version_number: number | null; manual_revision: string | null; new_entries: number; changed_entries: number; removed_entries: number; changes: Array<{ key: string; change_type: string; old_data: Record<string, unknown> | null; new_data: Record<string, unknown> | null; changed_at: string | null }> };
  return {
    versionNumber: data.version_number,
    manualRevision: data.manual_revision,
    newEntries: data.new_entries,
    changedEntries: data.changed_entries,
    removedEntries: data.removed_entries,
    changes: data.changes.map((item) => ({ key: item.key, changeType: item.change_type, oldData: item.old_data, newData: item.new_data, changedAt: item.changed_at })),
  };
}
